/* eslint-disable @typescript-eslint/no-explicit-any */
import { SearchOutlined } from '@ant-design/icons'
import {
  DefineCampaignStatus,
  DefineDiscountCampaignType,
} from 'app/api/marketing/discount-campaign/constant'
import {
  CampaignStatusEnum,
  CampaignTypeEnum,
} from 'app/api/marketing/discount-campaign/model'
import R from 'app/assets/R'
import { BaseCol } from 'app/components/common/BaseCol'
import { BaseRow } from 'app/components/common/BaseRow'
import { BaseForm } from 'app/components/common/forms/BaseForm'
import { BaseInput } from 'app/components/common/inputs/BaseInput'
import { BaseDatePicker } from 'app/components/common/pickers/BaseDatePicker'
import { BaseSelect } from 'app/components/common/selects/BaseSelect'
import { getArray } from 'app/components/tables/common-table/constant'
import { convertedVariables } from 'app/styles/themes/themeVariables'
import { Dayjs } from 'dayjs'
import { t } from 'i18next'
import { Dates, FONT_SIZE, FONT_WEIGHT, PADDING } from 'parkway-web-common'
import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { IFilterDataTableReport } from '../../type'
import { removeVietnameseTones } from 'app/helpers/string.helper'
import { usePagination } from 'app/hook'
import { IUnit } from 'app/model/unit.model'
import { useGetAreaList } from 'app/react-query/hook/location'
import { StatusEnum } from 'app/common/enum'
import { BaseTreeSelect } from 'app/components/common/selects/BaseTreeSelect'
import { transformDataSelectTree } from 'app/components/common/selects/BaseTreeSelect/helper'
import { notificationController } from 'app/controllers/notification-controller'

interface IProps {
  onChange?: (value: IFilterDataTableReport) => void
  formData?: IFilterDataTableReport
}

export const FilterLayout: React.FC<IProps> = ({ formData, onChange }) => {
  const { data: areaApi, isLoading: isLoadingArea } = useGetAreaList({
    typeid: 'area',
    status: StatusEnum.ACTIVE,
  })
  const [keywordSearchArea, setKeywordSearchArea] = useState('')

  const filterTreeNode = (value?: string, treeNode?: any) => {
    return removeVietnameseTones(treeNode?.title)
      .toLowerCase()
      .includes(removeVietnameseTones(value).toLowerCase())
  }

  const { flattenDataList } = usePagination()

  const areaList = useMemo(() => {
    const res: IUnit[] = flattenDataList(areaApi)
    return res?.map(item => ({
      ...item,
      childs: item?.childs
        ?.filter(child => !!child?._oldId)
        ?.map(item => ({
          ...item,
          _id: item?._oldId ?? item?._id,
        })),
    }))
  }, [areaApi])

  const onChangeStartDate = (day?: Dayjs) => {
    if (day && formData?.endDate && day.isAfter(formData.endDate)) {
      notificationController?.error({
        message: t(R.strings.error),
        description: t(R.strings.start_date_must_be_before_end_date),
      })
      return
    }

    const value = day ? day?.toISOString() : undefined
    onChange && onChange({ ...formData, startDate: value })
  }

  const onChangeEndDate = (day?: Dayjs) => {
    if (day && formData?.startDate && day.isBefore(formData.startDate)) {
      notificationController?.error({
        message: t(R.strings.error),
        description: t(R.strings.end_date_must_be_after_start_date),
      })
      return
    }

    const value = day ? day?.toISOString() : undefined
    onChange && onChange({ ...formData, endDate: value })
  }

  const onChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    onChange && onChange({ ...formData, name: value })
  }

  const onChangeClinic = (ids: string[]) => {
    onChange && onChange({ ...formData, clinics: ids })
  }

  const onChangeCampaignType = (value: CampaignTypeEnum[]) => {
    onChange && onChange({ ...formData, campaignType: value })
  }

  const onChangePriority = (value: number[]) => {
    onChange && onChange({ ...formData, priority: value })
  }

  const onChangeCampaignStatus = (value: CampaignStatusEnum[]) => {
    onChange && onChange({ ...formData, campaignStatus: value })
  }

  return (
    <BaseForm>
      <RootWrapper gutter={[16, 4]} align={'middle'}>
        <BaseCol xl={5}>
          <BaseFormItem hiddenLabel>
            <BaseInput
              prefix={<SearchOutlined rev={undefined} />}
              value={formData?.name}
              placeholder={t(R.strings.enter_campaign_name)}
              onChange={onChangeName}
              allowClear
            />
          </BaseFormItem>
        </BaseCol>
        <BaseCol xl={5}>
          <BaseFormItem hiddenLabel>
            <BaseSelectWrapper
              allowClear
              $prefix={t(R.strings.priority)}
              value={formData?.priority}
              placeholder={t(R.strings.all)}
              options={getArray(20).map(number => ({
                label: `${number}`,
                value: number,
              }))}
              mode="multiple"
              maxTagCount={0}
              $prefixWidth={80}
              onChange={(value: any) => onChangePriority(value as number[])}
              filterSort={(a, b) => a.value ?? 0 - b.value ?? 0}
            />
          </BaseFormItem>
        </BaseCol>
        <BaseCol xl={6}>
          <BaseFormItem hiddenLabel>
            <BaseSelectWrapper
              allowClear
              $prefix={t(R.strings.type_of_offer)}
              value={formData?.campaignType}
              $prefixWidth={80}
              placeholder={t(R.strings.all)}
              onChange={(value: any) =>
                onChangeCampaignType(value as CampaignTypeEnum[])
              }
              mode="multiple"
              maxTagCount={0}
              options={[
                {
                  label: t(
                    DefineDiscountCampaignType[CampaignTypeEnum.Bundle].keyI18n,
                  ),
                  value: CampaignTypeEnum.Bundle,
                },
                {
                  label: t(
                    DefineDiscountCampaignType[CampaignTypeEnum.BuyOneGetOne]
                      .keyI18n,
                  ),
                  value: CampaignTypeEnum.BuyOneGetOne,
                },
                {
                  label: t(
                    DefineDiscountCampaignType[CampaignTypeEnum.Voucher]
                      .keyI18n,
                  ),
                  value: CampaignTypeEnum.Voucher,
                },
                {
                  label: t(
                    DefineDiscountCampaignType[CampaignTypeEnum.Treatments]
                      .keyI18n,
                  ),
                  value: CampaignTypeEnum.Treatments,
                },
                {
                  label: t(
                    DefineDiscountCampaignType[CampaignTypeEnum.TotalBill]
                      .keyI18n,
                  ),
                  value: CampaignTypeEnum.TotalBill,
                },
              ]}
            />
          </BaseFormItem>
        </BaseCol>
        <BaseCol xl={8}>
          <BaseFormItem hiddenLabel>
            <BaseSelectWrapper
              allowClear
              $prefix={t(R.strings.apply_with_other_campaign)}
              value={formData?.applyWithOtherCampaign}
              $prefixWidth={200}
              placeholder={t(R.strings.all)}
              options={[
                {
                  label: t(R.strings.yes),
                  value: true,
                },
                {
                  label: t(R.strings.no),
                  value: false,
                },
              ]}
            />
          </BaseFormItem>
        </BaseCol>
        <BaseCol xl={6}>
          <BaseFormItem hiddenLabel>
            <BaseSelectWrapper
              allowClear
              $prefix={t(R.strings.status)}
              value={formData?.campaignStatus}
              $prefixWidth={75}
              placeholder={t(R.strings.all)}
              mode="multiple"
              onChange={value =>
                onChangeCampaignStatus(value as CampaignStatusEnum[])
              }
              maxTagCount={1}
              options={[
                {
                  label: t(
                    DefineCampaignStatus[CampaignStatusEnum.UpComing].keyI18n,
                  ),
                  value: CampaignStatusEnum.UpComing,
                },
                {
                  label: t(
                    DefineCampaignStatus[CampaignStatusEnum.OnGoing].keyI18n,
                  ),
                  value: CampaignStatusEnum.OnGoing,
                },
                {
                  label: t(
                    DefineCampaignStatus[CampaignStatusEnum.Completed].keyI18n,
                  ),
                  value: CampaignStatusEnum.Completed,
                },
                {
                  label: t(
                    DefineCampaignStatus[CampaignStatusEnum.Deactivated]
                      .keyI18n,
                  ),
                  value: CampaignStatusEnum.Deactivated,
                },
              ]}
            />
          </BaseFormItem>
        </BaseCol>
        <BaseCol xl={6}>
          <BaseFormItem hiddenLabel>
            <Picker
              $prefix={t(R.strings.start_date)}
              picker="date"
              value={
                formData?.startDate ? Dates.getDate(formData?.startDate) : null
              }
              onChange={day => onChangeStartDate(day as Dayjs)}
              allowClear
              format={'DD-MM-YYYY HH:mm'}
              placeholder={t(R.strings.all)}
              showTime
            />
          </BaseFormItem>
        </BaseCol>
        <BaseCol xl={6}>
          <BaseFormItem hiddenLabel>
            <Picker
              $prefix={t(R.strings.end_date)}
              value={
                formData?.endDate ? Dates.getDate(formData?.endDate) : null
              }
              picker="date"
              onChange={day => onChangeEndDate(day as Dayjs)}
              allowClear
              format={'DD-MM-YYYY HH:mm'}
              placeholder={t(R.strings.all)}
              showTime
            />
          </BaseFormItem>
        </BaseCol>
        <BaseCol xl={6}>
          <BaseFormItem hiddenLabel>
            <BaseTreeSelect
              placeholder={t(R.strings.select_clinic_apply)}
              loading={isLoadingArea}
              width={'100%'}
              treeData={transformDataSelectTree(areaList ?? [], {
                title: 'name',
                value: '_id',
                children: 'childs',
              })}
              value={formData?.clinics}
              multiple
              showSearch
              treeCheckable={true}
              onSearch={keyword => setKeywordSearchArea?.(keyword)}
              filterTreeNode={filterTreeNode}
              searchValue={keywordSearchArea}
              maxTagCount={0}
              onChange={ids => onChangeClinic?.(ids as string[])}
              allowClear
              style={{
                minWidth: 220,
              }}
            />
          </BaseFormItem>
        </BaseCol>
      </RootWrapper>
    </BaseForm>
  )
}

const RootWrapper = styled(BaseRow)`
  padding: ${PADDING.md};
`
const Picker = styled(BaseDatePicker)<{
  $prefix: string
  $prefixWidth?: number
}>`
  width: 100%;
  border: 1px solid ${convertedVariables.neutralBlack9Color};
  &:hover {
    border: 1px solid ${convertedVariables.neutralBlack9Color};
  }
  .ant-picker-input::before {
    content: '${props => props?.$prefix ?? '-'}:';
    font-size: ${FONT_SIZE.xs};
    font-weight: ${FONT_WEIGHT.medium};
    color: ${convertedVariables.primaryColor};
    opacity: 0.4;
    min-width: ${props => props?.$prefixWidth ?? 105}px;
  }
`

const BaseSelectWrapper = styled(BaseSelect)<{
  $prefix: string
  $prefixWidth?: number
}>`
  width: 100%;
  .ant-select-selector::before {
    content: '${props => props?.$prefix ?? '-'}:';
    font-size: ${FONT_SIZE.xs};
    font-weight: ${FONT_WEIGHT.medium};
    color: ${convertedVariables.primaryColor};
    opacity: 0.4;
    width: ${props => props?.$prefixWidth ?? 80}px;
    align-items: center;
    display: flex;
  }

  .ant-select-selection-placeholder {
    padding-left: ${props =>
      props.$prefixWidth ? `${props?.$prefixWidth}px` : 0};
  }
`

const BaseFormItem = styled(BaseForm.Item)`
  margin-bottom: 0px !important;
`
